import {
  ComponentFactoryResolver,
  Injectable,
  Injector,
  NgZone,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs/operators';

import { WorkspaceStateService } from './workspace-state.service';

/**
 * Hosts a content editor built as a popup (ContentSuburbComponent, ContentRegionComponent) in the side panel's body
 * (#256), with the same DynamicDialogConfig data its popup gets; its close() comes back to the caller. While one is
 * mounted, leaving the panel asks first: closing it discards unsaved changes.
 */
@Injectable()
export class EditorHostService {
  /** An editor is mounted in the panel. */
  open = false;

  private host: ViewContainerRef | null = null;

  constructor(
    private injector: Injector,
    private cfr: ComponentFactoryResolver,
    private zone: NgZone,
    private state: WorkspaceStateService
  ) {}

  /** The side panel's body, where editors mount (set by the side panel). */
  attach(host: ViewContainerRef): void {
    this.host = host;
  }

  /** The panel is going away, and its editor with it. */
  detach(): void {
    this.host = null;
    this.open = false;
  }

  mount<R>(component: Type<unknown>, data: unknown, closed: (result: R | undefined) => void): void {
    if (!this.host) return;
    this.host.clear();
    const ref = new DynamicDialogRef();
    const config = new DynamicDialogConfig();
    config.data = data;
    const injector = Injector.create({
      providers: [
        { provide: DynamicDialogRef, useValue: ref },
        { provide: DynamicDialogConfig, useValue: config },
      ],
      parent: this.injector,
    });
    this.host.createComponent(this.cfr.resolveComponentFactory(component), 0, injector);
    this.open = true;
    ref.onClose.pipe(take(1)).subscribe((result: R | undefined) =>
      this.zone.run(() => {
        this.unmount();
        closed(result);
        this.state.redraw();
      })
    );
    this.state.redraw();
  }

  unmount(): void {
    this.host?.clear();
    this.open = false;
  }

  /**
   * Whether the panel may change: yes when no editor is mounted, or the person agrees to discard it (it is then
   * unmounted).
   */
  leave(): boolean {
    if (!this.open) return true;
    if (!window.confirm("Close the editor? Changes you haven't saved will be lost.")) return false;
    this.unmount();
    return true;
  }
}
